<script>
	const moment = require('moment');
	const utilities = require('utilities');
	const assignmentService = require('services/assignmentService');
	const Store = require('kvstore');
	const STORE_CONFIRM_PROP = 'suppressAssignmentRedoConfirmation';
	const model = require('model/model');
	const pubsub = require('events/pubsub');
	const logger = require('utilities/logger');

	const { authenticateWithGoogleClassroom, isKnownGoogleClass, isInvalidatingGoogleError } = require('utilities/googleClassroomUtilities');

	var updateDeadline = function (deadline) {
		return moment(deadline).add(1, 'days').subtract(1, 'minutes').valueOf();
	};

	module.exports = {
		modelData: ['localGCAccessToken', 'gcClassList'],
		components: {
			'info-icon': require('components/svg/info-icon.vue').default,
			message: require('components/message.vue').default
		},
		data: function () {
			return {
				student: null,
				studentLabel: '',
				assessment: null,
				suppressConfirmation: false,
				assignmentId: null,
				deadline: null,
				roster: null
			};
		},
		mounted() {
			this.suppressConfirmation = Store.load(STORE_CONFIRM_PROP) || false;
			this.$watch('suppressConfirmation', function (v) {
				Store.save(STORE_CONFIRM_PROP, v);
			});

			this.$subscribe(
				'modal-opened',
				function (modalId, options) {
					if (modalId !== 'modal-reassign') {
						return;
					}

					if (!options.student || !options.assessment) {
						utilities.closeModals();
						logger.error('Reassign modal opened missing parameters');
					}
					let now = Date.now() / 1000;
					this.student = options.student;
					this.assessment = options.assessment;
					this.assignmentId = options.assignmentId;
					this.roster = options.roster;
					// If the assignment deadline is in the future
					if (this.assessment && this.assessment.deadline && this.assessment.deadline > now) {
						this.deadline = this.assessment.deadline * 1000;
					} else {
						// Else use today
						this.deadline = moment().startOf('day').valueOf();
					}
					this.buildLabel();
				}.bind(this)
			);
		},
		computed: {
			gcTokenMismatch() {
				const roster = this.roster;
				const gcid = roster && roster.gcid;

				return gcid && this.gcClassList && !isKnownGoogleClass(gcid);
			},
			needsGoogleAuth() {
				const assessment = this.assessment;
				const gcid = assessment && assessment.gcid;
				const token = this.localGCAccessToken;
				/* No GCA check needed - changes not applied to attachment */

				return gcid && (!token || this.gcTokenMismatch);
			}
		},
		methods: {
			goBack: function () {
				utilities.openModal('#modal-reassign', this);
			},
			buildLabel: function () {
				var studentsArray = [];
				Object.keys(this.assessment.students).forEach(
					function (k) {
						var s = $.extend(true, {}, this.assessment.students[k]);
						s.answers = null;
						s.uid = k;
						studentsArray.push(s);
					}.bind(this)
				);

				var labels = utilities.unambiguousStudentLabels(studentsArray);

				this.studentLabel = labels[this.student.uid];
			},
			redoAssignment: function () {
				let studentLabel = this.studentLabel;
				let assignmentLabel = this.assessment.product.title;
				let params = {
					uid: this.student.uid,
					aid: this.assignmentId
				};

				if (this.deadline) {
					params.edd = updateDeadline(this.deadline);
				}

				if (this.localGCAccessToken) {
					params.token = this.localGCAccessToken;
				}

				assignmentService
					.redoAssignment(params)
					.then(() => {
						pubsub.publish('message-success', '' + studentLabel + ' will redo "' + assignmentLabel + '"');

						// reload the assessment since it no longer reflects this student's state accurately
						Store.call('$broadcast', 'assessment-needs-reload', true);
					})
					.catch(error => {
						if (isInvalidatingGoogleError(error)) {
							model.set('localGCAccessToken', '');
						}
					});

				utilities.closeModals();
			},
			reviseAssignment: function () {
				let studentLabel = this.studentLabel;
				let assignmentLabel = this.assessment.product.title;
				let params = {
					uid: this.student.uid,
					aid: this.assignmentId
				};

				if (this.deadline) {
					params.edd = updateDeadline(this.deadline);
				}

				if (this.localGCAccessToken) {
					params.token = this.localGCAccessToken;
				}

				assignmentService
					.reviseAssignment(params)
					.then(() => {
						pubsub.publish('message-success', '' + studentLabel + ' will revise "' + assignmentLabel + '"');

						// reload the assessment since it no longer reflects this student's state accurately
						Store.call('$broadcast', 'assessment-needs-reload', false);
					})
					.catch(error => {
						if (isInvalidatingGoogleError(error)) {
							model.set('localGCAccessToken', '');
						}
					});

				utilities.closeModals();
			},
			authenticateWithGoogle() {
				authenticateWithGoogleClassroom();
			}
		}
	};
</script>
<style lang="scss">
	#modal-reassign {
		.date {
			text-align: center;
			& > div {
				display: inline-block;
				&.form-row-input {
					width: auto;
				}
			}
		}
	}
</style>
<template>
	<div>
		<div
			id="modal-reassign"
			class="modal"
			data-modal-window>
			<div class="modal-container">
				<div class="modal-window">
					<div class="modal-window-wrap">
						<button
							class="modal-close"
							data-modal-close>
							<span aria-hidden="true">&times;</span>
							<span class="hidden-visually"> Close</span>
						</button>
						<section
							v-if="assessment"
							class="modal-content">
							<header class="modal-content-header">
								<h2>Reassign to {{ studentLabel }}</h2>
								<h3>{{ assessment.product.title }}</h3>
							</header>
							<div class="modal-content-main">
								<div
									v-if="deadline"
									class="date margin-bottom-medium">
									<div class="label margin-right-medium">
										Due Date
										<button
											class="button -link"
											v-tooltip="'Students will be able to continue working on Article and Paired Text assignments after the due date passes.'">
											<info-icon></info-icon>
										</button>
									</div>
									<date-picker :date.sync="deadline"> </date-picker>
								</div>
								<div
									v-if="needsGoogleAuth"
									class="text-centered">
									<message
										v-if="gcTokenMismatch"
										class="text-left"
										type="error">
										Please authenticate the Google account used to manage this Classroom
									</message>
									<button
										class="button margin-bottom-small"
										@click="authenticateWithGoogle">
										Authenticate with Google
									</button>
								</div>
								<div class="text-centered">
									<span>
										<button
											v-if="suppressConfirmation"
											class="button"
											:class="{
												disabled: needsGoogleAuth
											}"
											:disabled="needsGoogleAuth"
											@click.prevent="redoAssignment">
											Redo
										</button>
										<button
											v-else
											class="button"
											:class="{
												disabled: needsGoogleAuth
											}"
											:disabled="needsGoogleAuth"
											data-modal="#modal-reassign-redo">
											Redo
										</button>
										<button
											class="button -link small-icon"
											v-tooltip="'Student will start from scratch. Previously submitted work will be erased.'">
											<info-icon aria-label="Redo"></info-icon>
										</button>
									</span>
									&nbsp;&nbsp;&nbsp;&nbsp; &nbsp;&nbsp;&nbsp;&nbsp; &nbsp;&nbsp;&nbsp;&nbsp;
									<span>
										<button
											class="button"
											:class="{
												disabled: needsGoogleAuth
											}"
											:disabled="needsGoogleAuth"
											@click.prevent="reviseAssignment">
											Revise
										</button>
										<button
											class="button -link small-icon"
											v-tooltip="'Student will resume work just as he or she left it.'">
											<info-icon aria-label="Revise"></info-icon>
										</button>
									</span>
								</div>
							</div>
						</section>
					</div>
				</div>
			</div>
		</div>
		<!-- end #modal-reassign -->
		<div
			id="modal-reassign-redo"
			class="modal"
			data-modal-window>
			<div class="modal-container">
				<div class="modal-window">
					<div class="modal-window-wrap">
						<button
							class="modal-close"
							data-modal-close>
							<span aria-hidden="true">&times;</span>
							<span class="hidden-visually"> Close</span>
						</button>
						<section class="modal-content">
							<header class="modal-content-header">
								<h2>Reassign Confirmation</h2>
							</header>
							<div
								v-if="assessment"
								class="modal-content-main">
								<div class="text-centered">
									<p>
										<strong> Are you sure you want {{ studentLabel }} to redo the assignment &ldquo;{{ assessment.product.title }}&rdquo;? </strong>
									</p>
									<p>All previously submitted answers will be erased.</p>
									<button
										class="button -outline"
										@click.prevent="goBack">
										Go Back
									</button>
									<button
										class="button"
										@click.prevent="redoAssignment">
										OK
									</button>
									<br />
									<div class="form">
										<auto-form-row
											type="checkbox"
											label="Do not show this message again"
											v-bind:data.sync="suppressConfirmation">
										</auto-form-row>
									</div>
								</div>
							</div>
						</section>
					</div>
				</div>
			</div>
		</div>
		<!-- end #modal-reassign-redo -->
	</div>
</template>
